/**
 * appStatus.js — Fonte única de verdade para mapeamento de status de aplicativos.
 *
 * Substitui os STATUS_MAP duplicados que existiam em:
 *   - src/components/organisms/MyApps/MyApps.vue
 *   - src/views/AppDetailsView/AppDetailsView.vue
 *   - src/components/molecules/AmStatusModal/AmStatusModal.vue
 *
 * Chaves canônicas: kebab-case espelhando o enum backend (AppStatus.php).
 */

// ---------------------------------------------------------------------------
// normalizeStatusKey
// ---------------------------------------------------------------------------

/**
 * Normaliza qualquer variação de nome de status para kebab-case canônico.
 *
 * Exemplos:
 *   'inDraft'          -> 'in-draft'
 *   'in_draft'         -> 'in-draft'
 *   'IN_DRAFT'         -> 'in-draft'
 *   'development'      -> 'development'
 *   'adjustmentPending'-> 'adjustment-pending'
 *
 * @param {string|null|undefined} value
 * @returns {string}
 */
export function normalizeStatusKey(value) {
  if (!value) return ''

  // Já está em kebab-case com hífen — retorna direto.
  if (/^[a-z]+(-[a-z]+)*$/.test(value)) return value

  // camelCase / PascalCase -> kebab-case
  const kebab = value
    // Insere hífen antes de letras maiúsculas (camelCase/PascalCase)
    .replace(/([A-Z])/g, (match) => `-${match.toLowerCase()}`)
    // snake_case -> hífen
    .replace(/_/g, '-')
    // Remove hífen inicial se houver
    .replace(/^-/, '')
    .toLowerCase()

  return kebab
}

// ---------------------------------------------------------------------------
// STATUS_MAP — fonte única para badge type e i18n key
// ---------------------------------------------------------------------------

/**
 * Mapa canônico de status.
 *
 * Chave: kebab-case do enum backend.
 * Campos:
 *   - tag        : chave camelCase usada nas i18n keys dos locales (ex: myApps.status.{tag})
 *   - type       : tipo do AmChip (badge color)
 *   - severity   : alias semântico (info, warning, error, success, neutral) — para uso futuro
 *   - modalTag   : chave usada nos locales do AmStatusModal (onde divergia do tag principal)
 */
export const STATUS_MAP = {
  'in-draft': {
    tag: 'configuring',
    type: 'lightGray',
    severity: 'neutral',
    modalTag: 'configuring'
  },
  'development': {
    tag: 'configuring',
    type: 'lightGray',
    severity: 'neutral',
    modalTag: 'configuring'
  },
  'in-test': {
    tag: 'inTest',
    type: 'warning',
    severity: 'warning',
    modalTag: 'inTest'
  },
  'in-analysis': {
    tag: 'inAnalysis',
    type: 'systemBlue120',
    severity: 'info',
    modalTag: 'inAnalysis'
  },
  'adjustment-pending': {
    tag: 'adjustmentPending',
    type: 'error',
    severity: 'error',
    modalTag: 'adjustmentPending'
  },
  'homolog': {
    tag: 'homolog',
    type: 'systemBlue120',
    severity: 'info',
    modalTag: 'homolog'
  },
  'published': {
    tag: 'published',
    type: 'success',
    severity: 'success',
    modalTag: 'published'
  },
  'reproved': {
    tag: 'reproved',
    type: 'error',
    severity: 'error',
    modalTag: 'reproved'
  },
  'deactivate': {
    tag: 'deactivate',
    type: 'primaryDarkBlue20',
    severity: 'neutral',
    modalTag: 'deactivate'
  }
}

// ---------------------------------------------------------------------------
// getStatusBadgeProps
// ---------------------------------------------------------------------------

/**
 * Retorna as propriedades do badge para um determinado status.
 *
 * @param {string|null|undefined} statusName - Nome do status (kebab, camel ou snake)
 * @returns {{ tag: string, type: string, severity: string, modalTag: string }|null}
 */
export function getStatusBadgeProps(statusName) {
  const key = normalizeStatusKey(statusName)
  const entry = STATUS_MAP[key]

  if (!entry) {
    if (import.meta.env.DEV) {
      console.warn(`[appStatus] Status desconhecido: "${statusName}" (normalizado: "${key}"). Verifique se o backend retornou um valor válido.`)
    }
    return null
  }

  return entry
}

// ---------------------------------------------------------------------------
// getSubstate
// ---------------------------------------------------------------------------

/**
 * Retorna o substate canônico de um status.
 * Útil para diferenciar 'in-draft' de 'development' dentro do mesmo step 'configuring'
 * sem alterar o stepper visual que os colapsa.
 *
 * @param {string|null|undefined} statusName
 * @returns {string} kebab-case canônico do status ('in-draft', 'development', etc.)
 */
export function getSubstate(statusName) {
  return normalizeStatusKey(statusName)
}

// ---------------------------------------------------------------------------
// flowStepFromStatus
// ---------------------------------------------------------------------------

/**
 * Mapeia um status para a etapa do fluxo de publicação.
 * Usado pela Fase 2 (AppFlowStepper) — preparado aqui mas não consumido ainda.
 *
 * @param {string|null|undefined} statusName
 * @returns {{ step: string, stepIndex: number|null, terminal: boolean }|null}
 */
export function flowStepFromStatus(statusName) {
  const key = normalizeStatusKey(statusName)

  const flowMap = {
    'in-draft':           { step: 'configuring',   stepIndex: 1, terminal: false },
    'development':        { step: 'configuring',   stepIndex: 1, terminal: false },
    'in-test':            { step: 'testing',       stepIndex: 2, terminal: false },
    'in-analysis':        { step: 'analyzing',     stepIndex: 3, terminal: false },
    'adjustment-pending': { step: 'analyzing',     stepIndex: 3, terminal: false },
    'homolog':            { step: 'homologating',  stepIndex: 4, terminal: false },
    'published':          { step: 'published',     stepIndex: 5, terminal: true  },
    'reproved':           { step: 'reproved',      stepIndex: null, terminal: true },
    'deactivate':         { step: 'deactivated',   stepIndex: null, terminal: true }
  }

  const entry = flowMap[key]

  if (!entry) {
    if (import.meta.env.DEV) {
      console.warn(`[appStatus] flowStepFromStatus: status desconhecido "${statusName}" (normalizado: "${key}").`)
    }
    return null
  }

  return entry
}
